<template>
  <div v-if="type === 'menu'" class="w-full">
    <Menubar :model="menuItems" />
  </div>
  <Select
    v-else
    v-model="selectedLocale"
    :options="locales"
    optionLabel="name"
    optionValue="locale"
    :class="[selectClass, 'langselect']"
    data-testid="language-select"
    @change="onLanguageChange"
  >
    <template #value>
      <div
        class="flex items-center"
        data-testid="language-current"
        :data-locale="selectedLocale?.locale"
      >
        <img
          v-if="selectedLocale?.img"
          :src="selectedLocale.img"
          :alt="selectedLocale.name"
          class="flag"
        />
      </div>
    </template>

    <template #option="slotProps">
      <div
        class="flex items-center"
        :data-testid="`language-option-${slotProps.option?.locale}`"
        :data-locale="slotProps.option?.locale"
      >
        <img
          v-if="slotProps.option?.img"
          :src="slotProps.option?.img"
          :alt="slotProps.option?.name"
          class="flag"
        />
        <span class="ml-2">{{ slotProps.option?.name }}</span>
      </div>
    </template>
  </Select>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import Menubar from 'primevue/menubar';

import { useLanguageSelector } from '@/composables/useLanguageSelector';

// Define props
const _props = defineProps({
  selectClass: {
    type: String,
    default: 'w-15', // Default value for the CSS class
  },
  type: {
    type: String,
    default: 'select', // Type can be 'select' or 'menu'
  },
});

const { locales, selectedLocale, selectLanguage } = useLanguageSelector();

function onLanguageChange(newLocale: any) {
  selectLanguage(newLocale.value);
}

// Menu items for the Menubar
const menuItems = ref([
  {
    label: '',
    icon: `flag-icon`, // Flag icon class for the selected locale
    items: locales.value.map((localeOption) => ({
      label: localeOption.name,
      icon: 'pi pi-fw pi-flag',
      command: () => {
        onLanguageChange({ value: localeOption.locale });
      },
    })),
  },
]);

// Watch for changes in the selectedLocale to update the Menubar icon
watch(selectedLocale, (newLocale) => {
  const localeKey = newLocale?.locale ?? selectedLocale.value?.locale ?? 'de';
  if (menuItems.value?.[0]) {
    menuItems.value[0].icon = `flag-icon-${localeKey}`;
  }
});
</script>

<style lang="css">
@layer app {
  .langselect.p-select:not(:hover) {
    border-color: var(--surface-ground);
    background-color: transparent;
  }

  .langselect .p-select-dropdown {
    display: none;
  }

  .flag {
    width: 1.2rem;
    height: 1.2rem;
  }

  .flag-icon {
    width: 1.2rem;
    height: 1.2rem;
  }

  .flag-icon-de {
    background-image: url('@/assets/images/flags/de.png');
  }

  .flag-icon-en {
    background-image: url('@/assets/images/flags/en.png');
  }

  .flag-icon-hu {
    background-image: url('@/assets/images/flags/hu.png');
  }

  .flag-icon-fr {
    background-image: url('@/assets/images/flags/fr.png');
  }
}
</style>
